import { Info } from "lucide-react";
import { useState, type FormEvent } from "react";
import { apiErrorMessage, request } from "../api/client.js";
import { BackToLanding } from "../design/components/BackToLanding.js";
import { PublicFooter, PublicHeader } from "./LandingPage.js";

export function createPreviewConfirmation() {
  return { status: "sent" as const, message: "Your enquiry was sent directly to the Conferis founder." };
}

export function EnquiryPage() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: "success" | "danger"; message: string } | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    setBusy(true);
    setResult(null);
    try {
      // This same-origin read creates the CSRF cookie before the protected public POST.
      await request("/api/v1/auth/session/");
      const response = await request<{ status: "sent"; message: string }>("/api/v1/enquiries/", {
        method: "POST",
        headers: { "Idempotency-Key": `web-enquiry-${crypto.randomUUID()}` },
        body: JSON.stringify({
          name: values.get("name"),
          email: values.get("email"),
          organization: values.get("organization"),
          event_size: values.get("eventSize"),
          message: values.get("message"),
        }),
      });
      setResult({ tone: "success", message: response.message });
      form.reset();
    } catch (reason) {
      setResult({ tone: "danger", message: apiErrorMessage(reason, "Your enquiry could not be sent. Please try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mk-site"><PublicHeader /><main className="mk-enquiry-main mobile-page-enter"><BackToLanding className="mk-enquiry-back" />
      <section className="mk-enquiry-intro" aria-labelledby="enquiry-title"><p>Send an enquiry</p><h1 id="enquiry-title">Tell us about your conference.</h1><span>Share the essentials below. Every enquiry goes directly to the Conferis founder for a personal response.</span></section>
      <section className="mk-enquiry-card" aria-labelledby="form-title"><div className="mk-enquiry-card-heading"><h2 id="form-title">Conference details</h2><p>A short overview is enough.</p></div><form onSubmit={submit}>
        <div className="mk-form-pair"><label>Your name<input name="name" autoComplete="name" minLength={2} maxLength={120} required /></label><label>Email address<input name="email" type="email" autoComplete="email" required /></label></div>
        <label>Organization<input name="organization" autoComplete="organization" maxLength={200} /></label>
        <label>Approximate event size<select name="eventSize" defaultValue="" required><option value="">Choose a range</option><option>Fewer than 100 people</option><option>100–499 people</option><option>500–999 people</option><option>1,000+ people</option><option>Not sure yet</option></select></label>
        <label>How can we help?<textarea name="message" rows={5} minLength={20} maxLength={5000} required /></label>
        <div className="mk-preview-disclosure" role="note"><Info aria-hidden="true" size={18} /><p><strong>You are speaking to the founder.</strong> Your details are used only to respond to this enquiry.</p></div>
        {result ? <div className={`mk-enquiry-result is-${result.tone}`} role={result.tone === "danger" ? "alert" : "status"}>{result.message}</div> : null}
        <button className="mk-enquiry-submit" type="submit" disabled={busy} aria-busy={busy || undefined}>{busy ? "Sending…" : "Send enquiry"}</button>
      </form></section>
    </main><PublicFooter /></div>
  );
}
